import {
  Box,
  Card,
  Chip,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { ChartData, ChartOptions } from "chart.js";
import { chartUtil } from "../../../common";
import { MediumCustomIcon } from "../../layer00";
import type { BoZoneTrendDirection } from "../../layer01";
import { direction2Chip } from "../../layer01/BoZoneTrendPill/BoZoneTrendPill";

export type BoZoneHistorySeries = {
  id: string;
  label: string;
  color: string;
  direction: BoZoneTrendDirection;
  points: chartUtil.ChartPoints;
};

type BoZoneHistoryChartProps = {
  title: string;
  labels: string[];
  pointLabels: string[];
  series: BoZoneHistorySeries[];
  isLoading?: boolean;
};

export const BoZoneHistoryChart = (props: BoZoneHistoryChartProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const gridColor = isDark ? "#3A3C3F" : "#ECEEF9";
  const tickColor = isDark ? "#989A9C" : "#53565A";

  const data: ChartData<"line", chartUtil.ChartPoints, string> = {
    labels: props.labels,
    datasets: props.series.map((node) => ({
      ...chartUtil.lineDefaults,
      label: node.label,
      data: node.points,
      borderColor: node.color,
      segment: chartUtil.gapSegment(node.color),
      borderWidth: 3,
      pointBackgroundColor: node.color,
      pointRadius: (ctx) => (ctx.raw === null ? 0 : 3),
      pointHoverRadius: 4,
    })),
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: "checkInSlot",
      intersect: false,
    },
    layout: {
      padding: {
        top: 8,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          color: tickColor,
          padding: 8,
          font: {
            family: "Lato",
            size: 12,
          },
        },
      },
      y: {
        min: chartUtil.scoreMin,
        max: chartUtil.scoreMax,
        grid: {
          color: gridColor,
          // Space between the labels and the plot. Unlike ticks.padding,
          // tickLength only adds horizontal space, so the plot keeps its height.
          tickLength: 24,
          tickColor: "transparent",
        },
        border: {
          display: false,
        },
        afterBuildTicks: (axis) => {
          axis.ticks = chartUtil.scoreBands.map((band) => ({
            value: band.value,
          }));
        },
        ticks: {
          color: tickColor,
          padding: 8,
          font: {
            family: "Lato",
            size: 12,
          },
          callback: (value) =>
            chartUtil.scoreBands.find((band) => band.value === value)?.label ??
            "",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        position: "checkInSlot",
        backgroundColor: isDark ? "#2A2B2D" : "#FFFFFF",
        titleColor: isDark ? "#FFFFFF" : "#001F4E",
        bodyColor: isDark ? "#BABBBD" : "#53565A",
        borderColor: gridColor,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6,
        titleFont: {
          family: "Figtree",
          size: 12,
          weight: "700",
        },
        bodyFont: {
          family: "Lato",
          size: 12,
        },
        callbacks: {
          title: (items) => props.pointLabels[items[0].dataIndex] ?? "",
          label: (item) =>
            ` ${item.dataset.label}: ${
              item.raw === null ? "-" : (item.parsed.y ?? 0).toFixed(1)
            }`,
        },
      },
    },
  };

  const hasData = props.series.some((node) => chartUtil.hasTrend(node.points));

  const canvasRef = chartUtil.useLineChart(data, options, [
    ...chartUtil.lineGlowPlugins,
    chartUtil.missingScoreMarkers,
  ]);

  const getChartBody = () => {
    if (props.isLoading) {
      return <Skeleton variant="rounded" height="100%" />;
    }
    if (hasData) {
      return <canvas ref={canvasRef} />;
    }
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Typography variant="cardBodyRegular" color="text.secondary">
          Complete more check-ins to see your score history.
        </Typography>
      </Stack>
    );
  };

  return (
    <Card
      sx={{
        borderRadius: ".75rem",
        paddingTop: "1.25rem",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        paddingBottom: "1.25rem",
      }}
    >
      <Stack direction="column" rowGap="1.25rem">
        <Stack
          direction="row"
          columnGap=".5rem"
          rowGap=".5rem"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Typography variant="cardSectionTitle">{props.title}</Typography>
          <Stack
            direction="row"
            columnGap=".5rem"
            rowGap=".5rem"
            alignItems="center"
            flexWrap="wrap"
          >
            {props.isLoading
              ? props.series.map((node) => (
                  <Skeleton
                    key={node.id}
                    variant="rounded"
                    width="5.5rem"
                    height="1.5rem"
                    sx={{ borderRadius: "1rem" }}
                  />
                ))
              : props.series.map((node) => (
                  <Chip
                    key={node.id}
                    label={node.label}
                    sx={{
                      paddingLeft: ".5rem",
                      paddingRight: ".5rem",
                      backgroundColor: alpha(node.color, 0.12),
                      color: node.color,
                    }}
                    icon={
                      <MediumCustomIcon
                        icon={direction2Chip[node.direction].icon}
                        stroke={node.color}
                        width={16}
                        height={16}
                      />
                    }
                  />
                ))}
          </Stack>
        </Stack>
        <Box height="13rem">{getChartBody()}</Box>
      </Stack>
    </Card>
  );
};
